import { Typography } from "antd";
import { apiHost } from "consts";
import bem from "easy-bem";
import CardSample from "features/cases/blocks/CommonCard";
import { useDataQueryRefetch } from "hooks/useData";
import { useIsMobile } from "hooks/useIsMobile";
import { useTexts } from "hooks/useTexts";
import { useUser } from "hooks/useUser";
import React, { useState } from "react";
import BubbleButton from "ui/BubbleButton";

import "./CaseContentMainCard.css";

const cn = bem("case-main-card");

// активная вкладка яркая, остальные сливаются с фоном своей группы
const tabColors = (active: boolean, groupBg: string, activeBg: string) => ({
  backgroundColor: active ? activeBg : groupBg,
  borderColor: active ? activeBg : groupBg,
});

type CaseContentMainCardProps = {
  kind: "problem" | "solution";
  problem: any;
  solution?: any;
  solutionList?: any[];
  probExpanded: boolean;
  setProbExpanded?: (v: boolean) => void;
};

const CaseContentMainCard: React.FC<CaseContentMainCardProps> = ({
  kind,
  problem,
  solution,
  solutionList = [],
  probExpanded,
  setProbExpanded,
}) => {
  const { isMobile } = useIsMobile();
  const { txt } = useTexts("case");
  const { isContributor, isBannedFromPublishing } = useUser();
  const [activeSolutionId, setActiveSolutionId] = useState<string | null>(null);

  const { data: generalData, refetch: refetchGeneral } =
    useDataQueryRefetch("general");
  const editorAddUrl = generalData?.nextSolutionId
    ? `#/problems/${problem?.id}/solutions/add`
    : "#";

  const canAddSolution = isContributor && !isBannedFromPublishing;
  const showTabs =
    kind === "problem" && (solutionList.length > 0 || canAddSolution);
  const tabSolution = showTabs
    ? solutionList.find((sol: any) => sol.id == activeSolutionId)
    : null;

  const renderTabs = () => (
    <div className={cn("tabs")}>
      <div className={cn("tabs-scroll")}>
        <div className={cn("tab-group", { problem: true })}>
          <BubbleButton
            size="s"
            text={txt("problem", "Проблема")}
            onClick={() => setActiveSolutionId(null)}
            {...tabColors(!tabSolution, "#fff2cb", "#FCD757")}
          />
        </div>
        {solutionList.length > 0 && (
          <div className={cn("tab-group", { solutions: true })}>
            {solutionList.map((sol: any, i: number) => (
              <BubbleButton
                key={sol.id}
                size="s"
                text={`${txt("solution", "Решение")} ${i + 1}`}
                onClick={() => setActiveSolutionId(sol.id)}
                {...tabColors(tabSolution?.id === sol.id, "#b7eaff", "#22AAE0")}
              />
            ))}
          </div>
        )}
      </div>
      {canAddSolution && (
        <div className={cn("tabs-add")}>
          {solutionList.length > 0 ? (
            <BubbleButton
              size="s"
              square
              text="+"
              onClick={() => {
                window.location.href = editorAddUrl;
                refetchGeneral();
              }}
              backgroundColor="#22AAE0"
              borderColor="#22AAE0"
            />
          ) : (
            <BubbleButton
              size="s"
              text={`+ ${txt("add_solution", "Добавить решение")}`}
              onClick={() => {
                window.location.href = editorAddUrl;
                refetchGeneral();
              }}
              backgroundColor="#fff"
              borderColor="#fff"
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {kind === "problem" && (
        <div
          id="topcard"
          className={cn({
            problem: !tabSolution,
            solution: !!tabSolution,
            tabbed: showTabs,
            mobile: isMobile,
          })}
        >
          {showTabs ? (
            renderTabs()
          ) : (
            <Typography.Title
              level={4}
              className={cn("title")}
            >
              {probExpanded ? (
                <em
                  className={cn("label")}
                  onClick={() => setProbExpanded?.(!probExpanded)}
                >
                  {txt("problem", "Проблема")}
                </em>
              ) : (
                <BubbleButton
                  size="m"
                  onClick={() => setProbExpanded?.(!probExpanded)}
                  text={txt("view_problem", "Посмотреть проблему")}
                />
              )}
            </Typography.Title>
          )}

          {tabSolution && (
            <CardSample
              parentData={{
                isProblem: false,
                isSolution: true,
                isAttribute: false,
                problemId: problem?.id,
                solutionId: tabSolution.id,
              }}
              cardContentId={tabSolution.id}
              sectionTitle={txt("solution", "Решение")}
              objectColor="#B7EAFF"
              highlightColor="#22AAE0"
              mode="wide"
              key={"tab" + tabSolution.id}
              meta={tabSolution.meta}
              title={tabSolution.title}
              description={tabSolution.description}
              url={`#/problems/${problem?.id}/solutions/${tabSolution.id}/`}
              editUrl={`${apiHost}/admin/solutions/solution/${tabSolution.id}/change/`}
              linkButtonText={
                isContributor
                  ? txt("more_details", "Подробнее")
                  : txt("more_details_solution", "Подробнее о решении")
              }
            />
          )}

          {!tabSolution && probExpanded && (
            <CardSample
              parentData={{
                isProblem: true,
                sphereId: problem?.sphere,
                isSolution: false,
                isAttribute: false,
                problemId: problem?.id,
              }}
              cardContentId={problem?.id}
              sectionTitle={txt("problem", "Проблема")}
              objectColor="#FFF2CB"
              highlightColor="#fcd757"
              mode="wide"
              key="top"
              meta={problem?.meta}
              title={problem?.title}
              description={problem?.description}
              editUrl={`${apiHost}/admin/solutions/problem/${problem?.id}/change/`}
              linkButtonText={
                isContributor
                  ? txt("more_details", "Подробнее")
                  : txt("more_details_problem", "Подробнее о проблеме")
              }
            />
          )}
        </div>
      )}

      {!!solution && (
        <div
          id="secondcard"
          className={cn({ solution: true, mobile: isMobile })}
        >
          <Typography.Title
            level={4}
            className={cn("title")}
          >
            {txt("solution", "Решение")}
          </Typography.Title>

          <CardSample
            parentData={{
              isProblem: false,
              isSolution: true,
              isAttribute: false,
              problemId: problem?.id,
              solutionId: solution?.id,
            }}
            cardContentId={solution?.id}
            sectionTitle={txt("solution", "Решение")}
            objectColor="#B7EAFF"
            highlightColor="#22AAE0"
            key="solution1"
            mode="wide"
            meta={solution?.meta}
            title={solution?.title}
            description={solution?.description}
            editUrl={`${apiHost}/admin/solutions/solution/${solution?.id}/change/`}
          />
        </div>
      )}
    </>
  );
};

export default CaseContentMainCard;
